import React, { useState } from 'react';
import { Button, InputNumber, Select, Typography } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import type { BoardGoods, BoardStop, BoardStopLine } from '../goodsRecipients';
import './TransportGoodsDropBoard.css';

const { Text } = Typography;
const SWATCHES = 8;
const TOLERANCE = 0.0005;
// Clicks on these never drop: the line's quantity, its unit label and buttons.
const CONTROLS_SELECTOR = 'input,button,.ant-input-number,.transport-drop-board__controls';
const titleId = (orderId: string) => `transport-drop-stop-title-${orderId}`;

interface Props {
  goods: BoardGoods[];
  stops: BoardStop[];
  addable: BoardStop[];
  warehouseLabel: string;
  /** Goods line dropped on a stop line of the same product. */
  onDrop: (lineId: string, stop: BoardStop, stopLine: BoardStopLine) => void;
  onQuantity: (stopLine: BoardStopLine, quantity: number | null) => void;
  onRemove: (stop: BoardStop, stopLine: BoardStopLine) => void;
  /** ✕ on the warehouse card: the rest nobody takes does not ride this truck. */
  onRemoveLeftover?: (item: BoardGoods) => void;
  onAddStop: (orderId: string) => void;
  /** Unloading order (B1, B2, …): move a stop on the truck up (-1) or down (+1). */
  onMoveStop?: (orderId: string, direction: -1 | 1) => void;
  /** A drop is still being applied: further drops are ignored until it ends. */
  busy?: boolean;
  /**
   * NZ.5: stop line key → what the line would get alone, for lines the form
   * gave less because other customers took the goods first (information only).
   */
  reducedByOthers?: Readonly<Record<string, number>>;
}

/**
 * Owner 2026-10-10, variant B („Gdzie jedzie”): drag a goods line (ZD) onto a
 * customer stop (ZK) — or click the goods, then the stop. One move puts the
 * ZK on the truck, pairs its line with the ZD line (freight to its margin)
 * and makes it an unloading stop; what nobody takes goes to the warehouse.
 * Rules: modules/transports/goodsRecipients.ts (buildDropBoard).
 */
const TransportGoodsDropBoard: React.FC<Props> = ({
  goods, stops, addable, warehouseLabel, onDrop, onQuantity, onRemove, onRemoveLeftover, onAddStop, onMoveStop, busy = false, reducedByOthers,
}) => {
  const { t, i18n } = useTranslation(['transport']);
  const [armed, setArmed] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ orderId: string; text: string } | null>(null);
  if (goods.length === 0) return null;
  const amount = (value: number | null, unit: string | null) => (value === null
    ? '—'
    : `${formatLocalizedQuantity(value, i18n.language)} ${formatUnit(unit, i18n.language)}`);
  const swatchOf = new Map(goods.map((item, index) => [item.line.itemId, index % SWATCHES]));
  // Name + dimensions (the name alone is often the same for several lines).
  const lineOf = new Map(goods.map((item) => [item.line.itemId, item.line]));
  // Unpaired stop line: its own (ZK line) name and dimensions.
  const goodsLabel = (lineId: string | null, fallback: string, fallbackDimensions?: string | null) => {
    const line = lineId ? lineOf.get(lineId) : undefined;
    const dimensions = line ? line.dimensions : fallbackDimensions;
    return (
      <>
        <span className="transport-drop-board__label-name">{line?.name ?? fallback}</span>
        {dimensions ? <span className="transport-drop-board__label-dims">{dimensions}</span> : null}
      </>
    );
  };

  const drop = (lineId: string | null, stop: BoardStop) => {
    setArmed(null);
    setOver(null);
    if (!lineId || busy) return;
    const item = goods.find((candidate) => candidate.line.itemId === lineId);
    if (!item) return;
    const sameProduct = stop.lines.filter((line) => line.productVariantId === item.line.productVariantId);
    // Review 2026-10-12: a line already paired with another ZD line is never
    // re-paired silently; re-dropping a line on its own pair is fine.
    const target = sameProduct.find((line) => !line.pairedLineId || line.pairedLineId === lineId);
    if (!target) {
      const product = [item.line.name, item.line.dimensions].filter(Boolean).join(', ');
      setNotice({
        orderId: stop.orderId,
        text: sameProduct.length > 0
          ? t('transport:dropBoard.allPaired')
          : t('transport:dropBoard.notOrdered', { product }),
      });
      return;
    }
    setNotice(null);
    onDrop(lineId, stop, target);
  };

  const onTruckCount = stops.filter((stop) => stop.onTruck).length;
  const leftovers = goods
    .map((item) => ({ item, left: item.carried === null ? null : Math.max(item.carried - item.assigned, 0) }))
    .filter(({ left }) => left === null || left > TOLERANCE);

  return (
    <section className={`transport-drop-board${busy ? ' is-busy' : ''}`} data-testid="transport-drop-board" aria-busy={busy || undefined}>
      <header className="transport-drop-board__head">
        <span className="transport-drop-board__purpose">{t('transport:dropBoard.purpose')}</span>
        <Text type="secondary" className="transport-drop-board__hint">{t('transport:dropBoard.hint')}</Text>
      </header>
      <div className="transport-drop-board__grid">
        <div className="transport-drop-board__pool" aria-label={t('transport:dropBoard.goods')}>
          {/* A ZD line set to „Nic” in „Co jedzie” is not on this truck: nothing to drop. */}
          {goods.filter((item) => item.carried === null || item.carried > TOLERANCE || item.assigned > TOLERANCE).map(({ line, carried, assigned }) => {
            const left = carried === null ? null : Math.max(carried - assigned, 0);
            // „Nic” on a line customers still take: never „✓ wszystko przypisane”.
            const notCarried = carried !== null && carried <= TOLERANCE && assigned > TOLERANCE;
            const done = !notCarried && left !== null && left <= TOLERANCE;
            return (
              <div
                key={line.itemId}
                role="button"
                tabIndex={0}
                draggable
                aria-pressed={armed === line.itemId}
                className={`transport-drop-board__goods${armed === line.itemId ? ' is-armed' : ''}${done ? ' is-done' : ''}`}
                data-testid={`transport-drop-goods-${line.itemId}`}
                onDragStart={(event) => { event.dataTransfer.setData('text/plain', line.itemId); setArmed(line.itemId); }}
                onDragEnd={() => setOver(null)}
                onClick={() => setArmed((current) => (current === line.itemId ? null : line.itemId))}
                onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setArmed((current) => (current === line.itemId ? null : line.itemId)); } }}
              >
                <span className={`transport-drop-board__swatch transport-zd-swatch-${swatchOf.get(line.itemId)}`} aria-hidden="true" />
                <div className="transport-drop-board__goods-text">
                  <span className="transport-drop-board__name">{line.name}</span>
                  {line.dimensions ? <span className="transport-drop-board__dims">{line.dimensions}</span> : null}
                  <span className="transport-drop-board__meta">
                    {[line.supplierOrderLabel, line.position ? t('transport:linePosition', { number: line.position }) : null].filter(Boolean).join(' · ')}
                  </span>
                  {notCarried ? (
                    <Text type="warning" className="transport-drop-board__not-carried" data-testid={`transport-drop-goods-not-carried-${line.itemId}`}>
                      {t('transport:dropBoard.notCarried', { assigned: amount(assigned, line.unit) })}
                    </Text>
                  ) : (
                    <span className={done ? 'transport-drop-board__done' : 'transport-drop-board__left'}>
                      {done
                        ? t('transport:dropBoard.allAssigned')
                        : t('transport:dropBoard.left', { left: amount(left, line.unit), carried: amount(carried, line.unit) })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="transport-drop-board__stops">
          {stops.map((stop, index) => {
            const relevant = stop.lines.filter((line) => line.pairedLineId || !stop.onTruck);
            const need = relevant.reduce((sum, line) => sum + (line.need ?? 0), 0);
            const got = relevant.reduce((sum, line) => sum + (line.onTruck ?? 0), 0);
            return (
              // a11y (review 2026-10-12): the stop is a group with its own fields
              // and buttons; the keyboard drop target is the „Upuść tu towar”
              // button. A mouse click anywhere outside the line controls (the
              // customer name included) still drops the armed goods.
              <div
                key={stop.orderId}
                role="group"
                aria-labelledby={titleId(stop.orderId)}
                className={`transport-drop-board__stop${stop.onTruck ? ' is-on' : ''}${armed ? ' is-armed' : ''}${over === stop.orderId ? ' is-over' : ''}`}
                data-testid={`transport-drop-stop-${stop.orderId}`}
                onDragOver={(event) => { event.preventDefault(); setOver(stop.orderId); }}
                onDragLeave={() => setOver((current) => (current === stop.orderId ? null : current))}
                onDrop={(event) => { event.preventDefault(); drop(event.dataTransfer.getData('text/plain') || armed, stop); }}
                onClick={(event) => { if (armed && !(event.target as HTMLElement).closest(CONTROLS_SELECTOR)) drop(armed, stop); }}
              >
                <div className="transport-drop-board__stop-head">
                  <span className={`transport-drop-board__pin${stop.onTruck ? '' : ' is-suggested'}`}>{stop.onTruck ? `B${index + 1}` : '+'}</span>
                  <div className="transport-drop-board__stop-title" id={titleId(stop.orderId)}>
                    <span className="transport-drop-board__customer">{stop.customerName}</span>
                    <span className="transport-drop-board__meta">
                      {[`ZK ${stop.orderLabel}`, stop.place].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  {stop.onTruck && onMoveStop && onTruckCount > 1 ? (
                    <span className="transport-drop-board__order">
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={index === 0}
                        aria-label={t('transport:dropBoard.moveUp', { customer: stop.customerName })}
                        data-testid={`transport-drop-stop-up-${stop.orderId}`}
                        onClick={() => onMoveStop(stop.orderId, -1)}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={index === onTruckCount - 1}
                        aria-label={t('transport:dropBoard.moveDown', { customer: stop.customerName })}
                        data-testid={`transport-drop-stop-down-${stop.orderId}`}
                        onClick={() => onMoveStop(stop.orderId, 1)}
                      />
                    </span>
                  ) : null}
                </div>
                {stop.lines.filter((line) => line.pairedLineId).map((line) => (
                  <div key={line.key} className="transport-drop-board__assigned" data-testid={`transport-drop-assigned-${line.key}`}>
                    <span className={`transport-drop-board__swatch transport-zd-swatch-${swatchOf.get(line.pairedLineId!) ?? 0}`} aria-hidden="true" />
                    <span className="transport-drop-board__assigned-name">{goodsLabel(line.pairedLineId, line.productName, line.dimensions)}</span>
                    <span className="transport-drop-board__controls">
                    {/* No min clamp (frontend-pages rule): a negative entry stays visible
                        and the cargo card's own rule blocks the save. */}
                    <InputNumber
                      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                      size="small"
                      value={line.onTruck}
                      aria-label={t('transport:dropBoard.quantityAria', { product: line.productName, customer: stop.customerName })}
                      onChange={(value) => onQuantity(line, value === null || value === undefined ? null : Number(value))}
                      style={{ width: 84 }}
                    />
                    <span className="transport-drop-board__unit">{formatUnit(line.unit, i18n.language)}</span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      aria-label={t('transport:dropBoard.remove', { product: line.productName, customer: stop.customerName })}
                      onClick={() => onRemove(stop, line)}
                    />
                    </span>
                    {/* NZ.5: on the line it concerns (review PR #433, LOW-2). */}
                    {reducedByOthers?.[line.key] !== undefined ? (
                      <Text type="secondary" className="transport-drop-board__reduced" data-testid={`transport-drop-reduced-${line.key}`}>
                        {t('transport:dropBoard.reducedByOthers', {
                          from: amount(reducedByOthers[line.key], line.unit),
                          to: amount(Number(line.onTruck) || 0, line.unit),
                        })}
                      </Text>
                    ) : null}
                  </div>
                ))}
                {!stop.onTruck ? (
                  <Text type="secondary" className="transport-drop-board__need">
                    {t('transport:dropBoard.needs', { items: stop.lines.map((line) => amount(line.need, line.unit)).join(' + ') })}
                  </Text>
                ) : null}
                {stop.onTruck && relevant.length > 0 ? (
                  got + TOLERANCE >= need
                    ? <span className="transport-drop-board__complete">{t('transport:dropBoard.complete')}</span>
                    // Owner 2026-10-12: a truck often brings only part of an order — not a warning.
                    : <span className="transport-drop-board__partial">{t('transport:dropBoard.partial', { got: amount(got, relevant[0]?.unit ?? null), need: amount(need, relevant[0]?.unit ?? null) })}</span>
                ) : null}
                {armed ? (
                  <Button
                    size="small"
                    type="dashed"
                    className="transport-drop-board__drop-button"
                    aria-label={t('transport:dropBoard.dropOnAria', { customer: stop.customerName, number: stop.orderLabel })}
                    data-testid={`transport-drop-stop-target-${stop.orderId}`}
                    onClick={() => drop(armed, stop)}
                  >
                    {t('transport:dropBoard.dropHere')}
                  </Button>
                ) : !stop.onTruck ? <span className="transport-drop-board__drop-here">{t('transport:dropBoard.dropHere')}</span> : null}
                {notice?.orderId === stop.orderId ? <Text type="warning">{notice.text}</Text> : null}
              </div>
            );
          })}

          <div className="transport-drop-board__stop is-warehouse" data-testid="transport-drop-warehouse">
            <div className="transport-drop-board__stop-head">
              <span className="transport-drop-board__pin is-warehouse">M</span>
              <div className="transport-drop-board__stop-title">
                <span className="transport-drop-board__customer">{t('transport:dropBoard.warehouse')}</span>
                <span className="transport-drop-board__meta">{warehouseLabel}</span>
              </div>
            </div>
            {leftovers.length === 0 ? (
              <Text type="secondary">{t('transport:dropBoard.warehouseNothing')}</Text>
            ) : leftovers.map(({ item, left }) => (
              <div key={item.line.itemId} className="transport-drop-board__assigned" data-testid={`transport-drop-warehouse-${item.line.itemId}`}>
                <span className={`transport-drop-board__swatch transport-zd-swatch-${swatchOf.get(item.line.itemId)}`} aria-hidden="true" />
                <span className="transport-drop-board__assigned-name">{goodsLabel(item.line.itemId, item.line.name)}</span>
                <span className="transport-drop-board__controls">
                  <span className="transport-drop-board__left">{amount(left, item.line.unit)}</span>
                  {onRemoveLeftover && left !== null ? (
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      aria-label={t('transport:dropBoard.removeLeftover', { product: [item.line.name, item.line.dimensions].filter(Boolean).join(' ') })}
                      title={t('transport:dropBoard.removeLeftoverTitle')}
                      data-testid={`transport-drop-warehouse-remove-${item.line.itemId}`}
                      onClick={() => onRemoveLeftover(item)}
                    />
                  ) : null}
                </span>
              </div>
            ))}
          </div>

          {addable.length > 0 ? (
            <Select<string>
              className="transport-drop-board__add"
              value={null as unknown as string}
              placeholder={t('transport:dropBoard.addCustomer')}
              data-testid="transport-drop-add-customer"
              showSearch
              optionFilterProp="label"
              // The list is as wide as its longest option (not the narrow field), so two ZK of one
              // customer show the full name and their numbers; `title` keeps the full text on hover.
              popupMatchSelectWidth={false}
              options={addable.map((stop) => ({
                value: stop.orderId,
                label: `${stop.orderLabel} · ${stop.customerName}`,
                title: `${stop.orderLabel} · ${stop.customerName}`,
              }))}
              onChange={(orderId) => onAddStop(orderId)}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default TransportGoodsDropBoard;
